import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { authService, AuthUser } from '../services/auth';
import { UserCredential } from 'firebase/auth';

interface AuthContextType {
  currentUser: AuthUser | null;
  loading: boolean;
  register: (email: string, password: string) => Promise<UserCredential>;
  login: (email: string, password: string) => Promise<UserCredential>;
  loginWithGoogle: () => Promise<UserCredential | null>;
  getRedirectResult: () => Promise<UserCredential | null>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (displayName?: string, photoURL?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthProvider mounted');
    let isMounted = true;

    // Check for redirect result
    const checkRedirectResult = async () => {
      try {
        const result = await authService.getRedirectResult();
        if (result?.user) {
          console.log('Redirect sign-in successful');
          // Auth state listener will handle setting the user
        }
      } catch (error) {
        console.error('Redirect result error:', error);
      }
    };

    // Process any pending redirect result
    checkRedirectResult();
    
    // Subscribe to auth state changes
    console.log('Setting up auth state listener');
    const unsubscribe = authService.onAuthStateChange((user) => {
      console.log('Auth state changed:', user ? 'Signed in' : 'Signed out');
      if (isMounted) {
        setCurrentUser(user);
        setLoading(false);
      }
    });

    // Force timeout to avoid indefinite loading
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        console.log('Auth loading timed out');
        setLoading(false);
      }
    }, 3000);

    return () => {
      console.log('Cleaning up AuthProvider');
      isMounted = false;
      unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [loading]);

  const value = {
    currentUser,
    loading,
    register: authService.register,
    login: authService.login,
    loginWithGoogle: authService.loginWithGoogle,
    getRedirectResult: authService.getRedirectResult,
    logout: authService.logout,
    resetPassword: authService.resetPassword,
    updateProfile: authService.updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <div className="auth-loading">{t('messages.loading')}</div> : children}
    </AuthContext.Provider>
  );
}; 